import React, { useMemo, useEffect, useRef } from 'react';
import { numberGreaterThanOrEqualTo } from 'redux-freeform';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  SidebarSingleContent,
  Box,
  constants,
  Center,
  Cluster,
  BoxWithShadow,
  Tabs,
  Modal,
  Spinner,
  Text,
  Title,
  util,
} from '@thecb/components';
import CartSidebar from '../../components/cart-sidebar';
import {
  CardCheckoutForm,
  CheckCheckoutForm,
  CashCheckoutForm,
  WirePaymentCheckoutForm,
  PayLinkCheckoutForm,
} from './components';
import AlertBar from '../../components/alert-bar';
import { useDispatch, useSelector } from 'react-redux';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';
import dayjs from 'dayjs';
import MagtekSwipeCardModal from './MagtekSwipeCardModal';
import { formatPrice } from '../../utils/helpers';
import { CARD_PAYMENT_TYPES } from '../../utils/constants';
import usePostVisitId from '../../utils/hooks/usePostVisitId';

export const PROCESSING_MODAL = 'PROCESSING_MODAL';
export const CARD_COUNTDOWN_MODAL = 'CARD_COUNTDOWN_MODAL';
export const MAGTEK_SWIPE_CARD_MODAL = 'MAGTEK_SWIPE_CARD_MODAL';

const Header = ({ alertBar, alertBarActions }) => {
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  const alertRef = useRef(null);

  // Scroll the alert into view whenever a new alert appears (e.g. a payment
  // error) so it isn't left off-screen when the user is scrolled down by the
  // process button. Applies to all tender types since the alert bar is shared.
  useEffect(() => {
    if (alertBar.alerts.length === 0) return;
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;
    // Defer to the next task so the scroll runs after the modal teardown and
    // its focus restoration, which otherwise scrolls the page back down to the
    // process button before our scroll would take effect.
    const id = setTimeout(() => {
      alertRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }, 0);
    return () => clearTimeout(id);
  }, [alertBar.alerts]);

  return (
    <Box width="100%" padding="16px 0">
      <Center maxWidth="85.714rem">
        {alertBar.alerts.length > 0 && (
          <div ref={alertRef}>
            <Box padding="16px 0">
              <AlertBar {...alertBar} {...alertBarActions} />
            </Box>
          </div>
        )}
        {sharedAlerts.length > 0 && (
          <Box padding="16px 0">
            <SharedAlertBar />
          </Box>
        )}
        <Title
          color={constants.colors.CHARADE_GREY}
          weight={constants.fontWeights.FONT_WEIGHT_BOLD}
        >
          Checkout
        </Title>
      </Center>
    </Box>
  );
};

const Checkout = ({
  actions,
  forms,
  alertBars,
  modals,
  makePayment,
  order,
  cancelTransaction,
  credit_card_reader_type,
  displayCreditCardReaderAlert,
  remainingBalance,
}) => {
  const flags = useFlags();
  const dispatch = useDispatch();
  usePostVisitId(order, dispatch);

  const cardActions = actions.forms.cardCheckoutForm.actions;
  const cardFields = forms.cardCheckoutForm.fields;
  const checkActions = actions.forms.checkCheckoutForm.actions;
  const checkFields = forms.checkCheckoutForm.fields;
  const cashActions = actions.forms.cashCheckoutForm.actions;
  const cashFields = forms.cashCheckoutForm.fields;
  const wireActions = actions.forms.wirePaymentCheckoutForm.actions;
  const wireFields = forms.wirePaymentCheckoutForm.fields;
  const paylinkActions = actions.forms.paylinkCheckoutForm.actions;
  const paylinkFields = forms.paylinkCheckoutForm.fields;

  const {
    fetching,
    order: { total: orderTotal, checkout_settings },
  } = useSelector((state) => state.cart);
  const { serviceFeeForCurrentAmount, convenienceFee, cardPaymentType } =
    useSelector((state) => state.checkout);
  const {
    isPollingForCheckResponse,
    isCheckProcessing,
    isPollingTimeExtended,
    timeRemainingToInsertCheck,
    timeRemainingToUseCard,
    isCancelingTransaction,
    checkScanFeedback,
  } = useSelector((state) => state.checkout);
  const cashSplitTenderAllowed =
    checkout_settings?.payment_tender_types?.cash?.split_tender_enabled;
  const checkSplitTenderAllowed =
    checkout_settings?.payment_tender_types?.check?.split_tender_enabled;
  const cardSplitTenderAllowed =
    checkout_settings?.payment_tender_types?.card?.split_tender_enabled;
  const wireSplitTenderAllowed =
    checkout_settings?.payment_tender_types?.wire_payment?.split_tender_enabled;

  useEffect(() => {
    return () => {
      // Reset all child form states when Checkout is unmounted
      cardActions.form.clear();
      checkActions.form.clear();
      cashActions.form.clear();
      wireActions.form.clear();
      paylinkActions.form.clear();
    };
  }, []);
  useEffect(() => {
    if (!fetching && orderTotal && !cashSplitTenderAllowed) {
      cashActions.fields.amount.addValidator(
        numberGreaterThanOrEqualTo(orderTotal),
      );
    }
    return () => {
      if (
        cashFields.amount.validators
          .map((v) => v.type)
          .includes('validator/NUMBER_GREATER_THAN')
      ) {
        // Clean up `numberGreaterThanOrEqualTo` validator if it exists
        cashActions.fields.amount.removeValidator(
          numberGreaterThanOrEqualTo(orderTotal),
        );
      }
    };
  }, [fetching, orderTotal, cashSplitTenderAllowed]);

  const processBtnText = (fields, isCreditTabSelected) =>
    useMemo(
      () =>
        `Process ${
          isCreditTabSelected
            ? formatPrice(
                Number(fields?.amount?.rawValue) +
                  Number(serviceFeeForCurrentAmount ?? 0) +
                  Number(convenienceFee ?? 0),
              )
            : formatPrice(Number(fields?.amount?.rawValue))
        }`,
      [fields?.amount?.rawValue, serviceFeeForCurrentAmount, convenienceFee],
    );

  const handleCancelTransaction = () => {
    cancelTransaction();

    if (!flags['pay-with-device-payment-service']) {
      actions.modals.checkoutModal.closeModal();
    }
  };

  const cardEnabled = checkout_settings?.payment_tender_types?.card?.enabled;
  const checkEnabled = checkout_settings?.payment_tender_types?.check?.enabled;
  const cashEnabled = checkout_settings?.payment_tender_types?.cash?.enabled;
  const wireEnabled =
    checkout_settings?.payment_tender_types?.wire_payment?.enabled;

  const isPayLinkEnabled =
    order?.order_items?.length > 0 &&
    order.order_items.every((item) => item?.paylink_enabled === true);

  const cardCheckoutFormComponent = (
    <CardCheckoutForm
      actions={cardActions}
      fields={cardFields}
      openProcessingModal={(modal) =>
        actions.modals.checkoutModal.openModal(modal)
      }
      makePayment={makePayment}
      creditCardReaderType={credit_card_reader_type}
      displayCreditCardReaderAlert={displayCreditCardReaderAlert}
      remainingBalance={remainingBalance}
      processBtnText={processBtnText(cardFields, true)}
      splitTenderAllowed={cardSplitTenderAllowed}
    />
  );

  const checkCheckoutFormComponent = (
    <CheckCheckoutForm
      actions={checkActions}
      fields={checkFields}
      openProcessingModal={(modal) =>
        actions.modals.checkoutModal.openModal(modal)
      }
      makePayment={makePayment}
      remainingBalance={remainingBalance}
      processBtnText={processBtnText(checkFields)}
      splitTenderAllowed={checkSplitTenderAllowed}
    />
  );

  const cashCheckoutFormComponent = (
    <CashCheckoutForm
      actions={cashActions}
      fields={cashFields}
      makePayment={makePayment}
      remainingBalance={remainingBalance}
      processBtnText={processBtnText(cashFields)}
      splitTenderAllowed={cashSplitTenderAllowed}
    />
  );

  const wireCheckoutFormComponent = (
    <WirePaymentCheckoutForm
      actions={wireActions}
      fields={wireFields}
      makePayment={makePayment}
      remainingBalance={remainingBalance}
      processBtnText={processBtnText(wireFields)}
      splitTenderAllowed={wireSplitTenderAllowed}
    />
  );

  const paylinkCheckoutFormComponent = (
    <PayLinkCheckoutForm
      actions={paylinkActions}
      fields={paylinkFields}
      forms={forms}
      remainingBalance={remainingBalance}
      processBtnText="Send PayLink"
      order={order}
    />
  );

  const tabsConfig = {
    tabs: [
      cardEnabled && {
        label: 'Credit Card',
        content: cardCheckoutFormComponent,
      },
      checkEnabled && {
        label: 'Check',
        content: checkCheckoutFormComponent,
      },
      cashEnabled && {
        label: 'Cash',
        content: cashCheckoutFormComponent,
      },
      wireEnabled && {
        label: 'Wire Transfer',
        content: wireCheckoutFormComponent,
      },
      isPayLinkEnabled && {
        label: 'PayLink',
        active: true,
        content: paylinkCheckoutFormComponent,
      },
    ].filter(Boolean),
  };

  const getCheckModalHeaderText = () =>
    isCheckProcessing || isPollingTimeExtended
      ? 'Processing Check'
      : isPollingForCheckResponse
        ? 'Awaiting Check'
        : null;

  const getCheckModalBodyText = () =>
    isCheckProcessing || isPollingTimeExtended
      ? ''
      : isPollingForCheckResponse
        ? `Please insert the check. This window will close in ${dayjs()
            .startOf('day')
            .add(timeRemainingToInsertCheck, 'second')
            .format('mm:ss')
            .slice(1)} ${
            timeRemainingToInsertCheck === 1 ? 'second' : 'seconds'
          }.`
        : null;

  const modalHeaderText = getCheckModalHeaderText();
  const modalBodyText = getCheckModalBodyText();

  const body = (
    <Box padding=".5rem 0 1.5rem">
      <BoxWithShadow
        background={constants.colors.WHITE}
        variant="baseStandard"
        padding="0"
        borderRadius="0.25rem"
      >
        <Modal
          noButtons
          modalHeaderText={modalHeaderText}
          defaultWrapper={false}
          modalOpen={isPollingForCheckResponse || isCheckProcessing}
          modalBodyText={
            <Center intrinsic tabIndex={0}>
              {/* when no buttons are rendered, a tabIndex must be explicitly defined 
              because focus-traps need at least one focusable element */}
              <Box padding="0" extraStyles="margin-bottom: 32px">
                <Text>{modalBodyText}</Text>
              </Box>
              {checkScanFeedback && (
                <Box
                  padding="12px 16px"
                  extraStyles="margin-bottom: 32px"
                  background={constants.colors.HAWKES_BLUE}
                >
                  <Text extraStyles="white-space: pre-line">
                    {checkScanFeedback}
                  </Text>
                </Box>
              )}
              <Spinner size="100" />
            </Center>
          }
        />
        <Modal
          modalHeaderText="Awaiting Card"
          defaultWrapper={false}
          onlyCloseButton={true}
          modalOpen={
            modals.checkoutModal.modalIsOpen &&
            modals.checkoutModal.modalId === CARD_COUNTDOWN_MODAL
          }
          hideModal={handleCancelTransaction}
          onExit={() => util.general.noop}
          closeButtonText="Cancel"
          isCloseActionDisabled={
            isCancelingTransaction && flags['pay-with-device-payment-service']
          }
          modalBodyText={
            <>
              <Cluster justify="center" childGap="4px">
                <Text>
                  {cardPaymentType === CARD_PAYMENT_TYPES.AUTOMATIC
                    ? 'Please have customer insert/swipe/tap the card within 1 minute.'
                    : 'Please follow the instructions on the credit card terminal. You must start within 1 minute.'}
                </Text>
                <Text>
                  {`This window will close in ${dayjs()
                    .startOf('day')
                    .add(timeRemainingToUseCard, 'second')
                    .format('mm:ss')
                    .slice(1)}.`}
                </Text>
              </Cluster>
              <Box padding="24px 0px 0px 0px">
                <Spinner size="100" />
              </Box>
            </>
          }
        />
        <Modal
          modalHeaderText="Processing"
          defaultWrapper={false}
          onlyCloseButton={true}
          modalOpen={
            modals.checkoutModal.modalIsOpen &&
            modals.checkoutModal.modalId === PROCESSING_MODAL
          }
          hideModal={handleCancelTransaction}
          onExit={() => util.general.noop}
          closeButtonText="Cancel"
          modalBodyText={
            <Center intrinsic>
              <Box>
                <Spinner size="100" />
              </Box>
            </Center>
          }
        />
        {/* // Wrapper needed to include keyboard listener hook for Magtek card reader */}
        <MagtekSwipeCardModal
          fields={cardFields}
          modals={modals}
          actions={actions}
          credit_card_reader_type={credit_card_reader_type}
        />
        {!order.order_id && (
          <Box padding="1.5rem">
            <Spinner size="100" />
          </Box>
        )}
        <Tabs
          tabsConfig={tabsConfig}
          extraStyles={`
          .tab-list-item:first-child { border-top-left-radius: 0.25rem }
          .tab-list-item:last-child { border-top-right-radius: 0.25rem }
        `}
        />
      </BoxWithShadow>
    </Box>
  );
  return (
    <SidebarSingleContent
      header={
        <Header
          key="CheckoutHeader"
          alertBar={alertBars.checkoutAlertBars}
          alertBarActions={actions.alertBars.checkoutAlertBars}
        />
      }
      mainContent={body}
      sidebarContent={<CartSidebar />}
      sidebarTargetWidth="27.429rem"
      sidebarWrapperMaxWidth="85.714rem"
      sidebarOnRight={true}
      childGap="0.5rem"
    />
  );
};

export default Checkout;
